import * as firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/auth';
import AppConfig from '@/plugins/config/AppConfig';
import * as collections from '@/constants/dbCollections';
import { invitationStatusIds } from '@/constants/Invitation.constants';

// Initialize the Firebase App depending on then env config and export the firestore DB.
const { config } = AppConfig.load();
export const db = firebase.initializeApp(config.firebaseSettings).firestore();

// Export types that exist in Firestore
// This is not always necessary, but it's used in other examples
export const { TimeStamp, GeoPoint } = firebase.firestore;


/* HELPER FUNCTIONS */
/* Declared as functions rather than expressions (arrow functions) so that
we can take advantage of hoisting when composing queries */

/**
 * GLOBAL HELPERS
 */
export function documentWithCreatedAtTimestamp(document) {
  return {
    ...document,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
  };
}

export function documentWithUpdatedAtTimestamp(document) {
  return {
    ...document,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  };
}

export function getDocumentById(collection, documentId) {
  return db.collection(collection).doc(documentId).get();
}

export function updateDocumentById(collection, documentId, updatedDocument) {
  const updatedDocumentWithTimestamps = documentWithUpdatedAtTimestamp(updatedDocument);
  return db.collection(collection).doc(documentId).update(updatedDocumentWithTimestamps);
}

export function addNewDocument(collection, newDocument) {
  const newDocumentWithTimestamps = documentWithCreatedAtTimestamp(documentWithUpdatedAtTimestamp(newDocument));
  return db.collection(collection).add(newDocumentWithTimestamps);
}

export function setNewDocument(collection, documentId, documentData) {
  const newDocumentWithTimestamps = documentWithCreatedAtTimestamp(documentWithUpdatedAtTimestamp(documentData));
  return db.collection(collection).doc(documentId).set(newDocumentWithTimestamps);
}

export function getDocumentReference(collection, documentId) {
  return db.collection(collection).doc(documentId);
}

export function getDocumentReferenceInSubcollection(collection, subcollection, collectionDocumentId, subcollectionDocumentId) {
  return db.collection(collection).doc(collectionDocumentId).collection(subcollection).doc(subcollectionDocumentId);
}

/**
 * USER QUERIES
 */
export function getUserByAuthId(authId) {
  return db.collection(collections.USERS)
    .where('authId', '==', authId)
    .get();
}

// Purpose of this query is to get the user that was last used by an auth account.
// In essence acts as a way to determine the default workspace.
export function getLastLoggedInUserByAuthId(uid) {
  return db.collectionGroup(collections.USERS)
    .where('uid', '==', uid)
    .where('isActive', '==', true)
    .orderBy('lastLoggedInAt', 'desc')
    .limit(1)
    .get();
}

export function getWorkspaceUserById(userId, workspaceId) {
  return db.collection(collections.WORKSPACE_USERS)
    .doc(workspaceId)
    .collection(collections.USERS)
    .doc(userId)
    .get();
}

export function getWorkspaceUsersByIds(userIds, workspaceId) {
  return userIds.map(userId => getWorkspaceUserById(userId, workspaceId));
}

export function getWorkspaceUsers(workspaceId) {
  return db.collection(collections.WORKSPACE_USERS)
    .doc(workspaceId)
    .collection(collections.USERS)
    .where('workspaceId', '==', workspaceId)
    .get();
}

// Gets the top-level workspaceUsers document, not the USERS subcollection.
export function getWorkspaceUsersDocument(workspaceId) {
  return db.collection(collections.WORKSPACE_USERS)
    .doc(workspaceId)
    .get();
}

export function addNewUser(userDefaultProfile) {
  const newUserDocument = { ...userDefaultProfile };
  newUserDocument.lastLoggedInAt = firebase.firestore.FieldValue.serverTimestamp();

  return addNewDocument(collections.USERS, newUserDocument);
}

export function updateUserById(userId, updatedUser, workspaceId) {
  const updatedDocumentWithTimestamps = documentWithUpdatedAtTimestamp(updatedUser);
  return db.collection(collections.WORKSPACE_USERS)
    .doc(workspaceId)
    .collection(collections.USERS)
    .doc(userId)
    .update(updatedDocumentWithTimestamps);
}

export function updateUserOOORanges(userId, workspaceId, oooRanges) {
  return updateUserById(userId, { oooRanges }, workspaceId);
}

export function updateUserLoginTimestamp(userId, workspaceId) {
  const updatedUser = { lastLoggedInAt: firebase.firestore.FieldValue.serverTimestamp() };
  return updateUserById(userId, updatedUser, workspaceId);
}

export async function archiveUser(userId, teamIds, workspaceId) {
  const updatedUser = { isActive: false };
  const userWithTimestamps = documentWithUpdatedAtTimestamp(updatedUser);
  const updatedTeams = teamIds.map(() => ({ userIds: firebase.firestore.FieldValue.arrayRemove(userId) }));
  const teamsWithTimestamps = updatedTeams.map(documentWithUpdatedAtTimestamp);

  const batch = db.batch();

  const teamRefs = teamIds.map(teamId => db.collection(collections.TEAMS).doc(teamId));
  const userRef = db.collection(collections.WORKSPACE_USERS)
    .doc(workspaceId)
    .collection(collections.USERS)
    .doc(userId);

  teamRefs.forEach((teamRef, index) => {
    batch.update(teamRef, teamsWithTimestamps[index]);
  });

  batch.update(userRef, userWithTimestamps);

  return batch.commit();
}


/**
 * GOAL QUERIES
 */
export function getGoalById(goalId) {
  return getDocumentById(collections.GOALS, goalId);
}

export function getGoalsByUserId(userId, workspaceId) {
  return (
    db.collection(collections.GOALS)
      .where('userId', '==', userId)
      .where('workspaceId', '==', workspaceId)
      .get()
  );
}

// Do not pass more than 10 dates, since that is the max for a firestore IN query.
export function getGoalByUserIdByDates(userId, dates, workspaceId) {
  return (
    db.collection(collections.GOALS)
      .where('userId', '==', userId)
      .where('goalDate', 'in', dates)
      .where('workspaceId', '==', workspaceId)
      .get()
  );
}

export function getGoalsByUserIds(userIds, workspaceId) {
  return userIds.map(userId => getGoalsByUserId(userId, workspaceId));
}

// Do not pass more than 10 dates, since that is the max for a firestore IN query.
export function getGoalsByUserIdsByDates(userIds, dates, workspaceId) {
  return userIds.map(userId => getGoalByUserIdByDates(userId, dates, workspaceId));
}

export function addNewGoal(newGoal) {
  return addNewDocument(collections.GOALS, newGoal);
}

export function updateGoalById(goalId, updatedGoal) {
  return updateDocumentById(collections.GOALS, goalId, updatedGoal);
}

export function getGoalsRefsByUserId(userId, workspaceId) {
  return db.collection(collections.GOALS)
    .where('userId', '==', userId)
    .where('workspaceId', '==', workspaceId);
}


/**
 * WORKSPACE QUERIES
 */
export function getWorkspaceById(workspaceId) {
  return getDocumentById(collections.WORKSPACES, workspaceId);
}

export function getUserWorkspaces(userId) {
  return db.collection(collections.WORKSPACES)
    .where('ownerId', '==', userId)
    .get();
}

export async function addNewWorkspace(newWorkspace, userId, newUser) {
  const workspaceWithTimestamps = documentWithCreatedAtTimestamp(documentWithUpdatedAtTimestamp(newWorkspace));
  const newUserWithTimestamps = documentWithCreatedAtTimestamp(documentWithUpdatedAtTimestamp(newUser));
  const batch = db.batch();

  const newWorkspaceRef = db.collection(collections.WORKSPACES).doc(); // Create a ref with auto-generated ID
  const newWorkspaceUsersRef = db.collection(collections.WORKSPACE_USERS).doc(newWorkspaceRef.id);
  const userRef = db.collection(collections.WORKSPACE_USERS)
    .doc(newWorkspaceRef.id)
    .collection(collections.USERS)
    .doc(userId);

  batch.set(newWorkspaceRef, workspaceWithTimestamps);
  batch.set(
    newWorkspaceUsersRef,
    documentWithUpdatedAtTimestamp(documentWithCreatedAtTimestamp({ workspaceId: newWorkspaceRef.id, userCount: 0 })),
  );
  batch.set(
    userRef,
    documentWithUpdatedAtTimestamp(documentWithCreatedAtTimestamp({
      ...newUserWithTimestamps,
      workspaceId: newWorkspaceRef.id,
      lastLoggedInAt: firebase.firestore.FieldValue.serverTimestamp(),
    })),
  );

  return batch.commit();
}

export function updateWorkspaceById(workspaceId, updatedWorkspace) {
  return updateDocumentById(collections.WORKSPACES, workspaceId, updatedWorkspace);
}

/**
 * TEAM QUERIES
 */
export function getWorkspaceTeams(workspaceId) {
  return db.collection(collections.TEAMS)
    .where('workspaceId', '==', workspaceId)
    .get();
}

export function getPublicWorkspaceTeams(workspaceId) {
  return db.collection(collections.TEAMS)
    .where('workspaceId', '==', workspaceId)
    .where('isPrivate', '==', false)
    .get();
}

export function addNewTeam(newTeam) {
  return addNewDocument(collections.TEAMS, newTeam);
}

export function updateTeam(teamId, updatedTeam) {
  return updateDocumentById(collections.TEAMS, teamId, updatedTeam);
}

export function getTeamById(teamId) {
  return getDocumentById(collections.TEAMS, teamId);
}

export function getTeamsByIds(teamIds) {
  return teamIds.map(teamId => getTeamById(teamId));
}

export function getTeamsByUserIdAndWorkspaceId(userId, workspaceId) {
  return db.collection(collections.TEAMS)
    .where('userIds', 'array-contains', userId)
    .where('workspaceId', '==', workspaceId)
    .get();
}

export function getPublicTeamsByUserId(userId, workspaceId) {
  return db.collection(collections.TEAMS)
    .where('userIds', 'array-contains', userId)
    .where('workspaceId', '==', workspaceId)
    .where('isPrivate', '==', false)
    .get();
}


/**
 * INVITATION QUERIES
 */
export function addNewInvitation(newInvitation) {
  return addNewDocument(collections.INVITATIONS, newInvitation);
}

export function addNewInvitations(newInvitations) {
  return newInvitations.map(addNewInvitation);
}

export function getInvitationById(invitationId) {
  return getDocumentById(collections.INVITATIONS, invitationId);
}

export function updateInvitationById(invitationId, updatedInvitation) {
  return updateDocumentById(collections.INVITATIONS, invitationId, updatedInvitation);
}

export function getWorkspaceInvitations(workspaceId) {
  return db.collection(collections.INVITATIONS)
    .where('workspaceId', '==', workspaceId)
    .get();
}

export function getPendingWorkspaceInvitations(workspaceId) {
  return db.collection(collections.INVITATIONS)
    .where('workspaceId', '==', workspaceId)
    .where('statusId', '==', invitationStatusIds.PENDING)
    .get();
}

export function getPendingUserInvitationsByEmail(userEmail) {
  return db.collection(collections.INVITATIONS)
    .where('statusId', '==', invitationStatusIds.PENDING)
    .where('inviteeEmail', '==', userEmail)
    .get();
}

export function getUserInvitationsByEmail(userEmail) {
  return db.collection(collections.INVITATIONS)
    .where('inviteeEmail', '==', userEmail)
    .get();
}

export function getInvitationsByTeamId(teamId) {
  return db.collection(collections.INVITATIONS)
    .where('teamId', '==', teamId)
    .get();
}

export async function acceptWorkspaceInvitation(invitationId, updatedInvitation, newUser, teamId, workspaceId) { // eslint-disable-line max-len
  const updatedTeam = { userIds: firebase.firestore.FieldValue.arrayUnion(newUser.uid) };
  const invitationWithTimestamps = documentWithUpdatedAtTimestamp(updatedInvitation);
  const userWithTimestamps = documentWithUpdatedAtTimestamp(documentWithCreatedAtTimestamp(newUser));
  userWithTimestamps.lastLoggedInAt = firebase.firestore.FieldValue.serverTimestamp();
  const teamWithTimestamps = documentWithUpdatedAtTimestamp(updatedTeam);

  const batch = db.batch();

  const invitationRef = db.collection(collections.INVITATIONS).doc(invitationId);
  const userRef = db.collection(collections.WORKSPACE_USERS).doc(workspaceId).collection(collections.USERS).doc(newUser.uid); // eslint-disable-line max-len
  const teamRef = db.collection(collections.TEAMS).doc(teamId);

  batch.update(invitationRef, invitationWithTimestamps);
  batch.set(userRef, userWithTimestamps);
  batch.update(teamRef, teamWithTimestamps);

  return batch.commit();
}

/**
 * STANDUP QUERIES
 */
export function getStandupById(standupId) {
  return getDocumentById(collections.STANDUPS, standupId);
}

export function getStandupsByIds(standupIds) {
  return standupIds.map(standupId => getStandupById(standupId));
}

export function addNewStandup(newStandup) {
  return addNewDocument(collections.STANDUPS, newStandup);
}

export function getCurrentDayStandupByTeam(teamId) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return db.collection(collections.STANDUPS)
    .where('teamId', '==', teamId)
    .where('createdAt', '>=', today)
    .where('createdAt', '<', tomorrow)
    .get();
}

export function updateStandupById(standupId, updatedStandup) {
  return updateDocumentById(collections.STANDUPS, standupId, updatedStandup);
}

/**
 * SUBSCRIPTION QUERIES
 */
export function addNewSubscription(newSubscription) {
  return addNewDocument(collections.SUBSCRIPTIONS, newSubscription);
}

export function updateSubscriptionById(subscriptionId, updatedSubscription) {
  return updateDocumentById(collections.SUBSCRIPTIONS, subscriptionId, updatedSubscription);
}

export function getSubscriptionById(subscriptionId) {
  return getDocumentById(collections.SUBSCRIPTIONS, subscriptionId);
}
export function getSubscriptionByWorkspaceId(workspaceId) {
  return db.collection(collections.SUBSCRIPTIONS)
    .where('workspaceId', '==', workspaceId)
    .limit(1)
    .get();
}

/**
 * MCP TOKEN QUERIES
 */
export function getMcpTokenByUserId(userId) {
  return db.collection(collections.MCP_TOKENS)
    .where('userId', '==', userId)
    .limit(1)
    .get();
}

export function setMcpToken(token, tokenData) {
  return setNewDocument(collections.MCP_TOKENS, token, tokenData);
}

export function deleteMcpToken(token) {
  return db.collection(collections.MCP_TOKENS).doc(token).delete();
}
